// apps/web/src/features/crm/leads/card/IntakeArtifactRow.tsx
//
// One call-center intake submission inside the "Artifacts" -> "In system"
// list (#2316, D-2). Renders as a compact SUMMARY row (name · phone · date)
// that expands in place to reveal the full detail the external agent
// submitted — outcome, source, submitter, comment, and the call recording.
// Re-houses `LeadIntakeHistorySection`'s retired `IntakeRow` (#1260) without
// dropping a field; the data source stays `useLeadIntakeHistory` (the full
// PII projection), never the thin `useLeadArtifacts` intake entity (#2314,
// which only carries `{kind, id, code: null, date}` and cannot show detail).
//
// SECURITY (D-3): the call-recording `audioUrl` renders as a clickable
// external anchor ONLY when it parses as an `https:` URL — never an
// <iframe>/<audio> player, and never a `javascript:`/`data:`/`http:`/
// unparseable href. The shared schema already rejects non-HTTPS schemes at
// submit time; this is a defense-in-depth render-time re-check because the
// internal projection types `audioUrl` as a plain `string | null`.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPhone, phoneSchema, type LeadIntakeSubmissionHistoryItem } from '@magimp/shared';
import { Icon } from '../../../../ui/index.js';
import { formatDateTime } from '../../../../i18n/index.js';

export interface IntakeArtifactRowProps {
  item: LeadIntakeSubmissionHistoryItem;
}

function renderPhone(phone: string): string {
  return phoneSchema.safeParse(phone).success ? formatPhone(phone) : phone;
}

/** `https:`-only guard (D-3) — never returns a `javascript:`/`data:`/`http:` href. */
function safeHttpsHref(raw: string | null): string | null {
  if (!raw) return null;
  try {
    return new URL(raw).protocol === 'https:' ? raw : null;
  } catch {
    return null;
  }
}

export function IntakeArtifactRow({ item }: IntakeArtifactRowProps) {
  const { t } = useTranslation(['leads']);
  const [expanded, setExpanded] = useState(false);
  const [hover, setHover] = useState(false);
  const audioHref = safeHttpsHref(item.audioUrl);

  return (
    <div data-testid="lead-intake-history-row">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        aria-expanded={expanded}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          padding: '6px 8px',
          margin: '0 -8px',
          borderRadius: 'var(--r-2)',
          background: hover ? 'var(--bg-2)' : 'transparent',
          border: 0,
          textAlign: 'left',
          cursor: 'pointer',
          font: 'inherit',
          color: 'inherit',
        }}
      >
        <Icon name="phone" size={14} style={{ color: 'var(--fg-3)', flex: 'none' }} />
        <span
          style={{
            minWidth: 0,
            fontSize: 13,
            color: 'var(--fg-1)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.name} · {renderPhone(item.phone)}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: 'var(--fg-3)', flex: 'none' }}>
          {formatDateTime(item.createdAt)}
        </span>
        <Icon
          name={expanded ? 'chevron-down' : 'chevron-right'}
          size={13}
          style={{ color: 'var(--fg-4)', flex: 'none' }}
        />
      </button>
      {expanded && (
        <div
          data-testid="lead-intake-detail"
          style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '2px 8px 8px' }}
        >
          <div style={{ fontSize: 12, color: 'var(--fg-2)' }}>
            {t(`leads:intake.outcome.${item.outcome}`)}
          </div>
          {item.sourceDisplayName && (
            <div style={{ fontSize: 12, color: 'var(--fg-2)' }} data-testid="lead-intake-source">
              {t('leads:intake.source')}: {item.sourceDisplayName}
            </div>
          )}
          {item.submittedByDisplayName && (
            <div style={{ fontSize: 12, color: 'var(--fg-2)' }} data-testid="lead-intake-submitter">
              {t('leads:intake.submittedBy')}: {item.submittedByDisplayName}
            </div>
          )}
          {item.comment && (
            <div style={{ fontSize: 12, color: 'var(--fg-2)' }} data-testid="lead-intake-comment">
              {item.comment}
            </div>
          )}
          {item.audioUrl &&
            (audioHref ? (
              <a
                href={audioHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="lead-intake-audio"
                style={{ fontSize: 12, color: 'var(--accent-1-press)' }}
              >
                {t('leads:intake.audioLink')}
              </a>
            ) : (
              <span data-testid="lead-intake-audio" style={{ fontSize: 12, color: 'var(--fg-3)' }}>
                {t('leads:intake.audioLink')}
              </span>
            ))}
        </div>
      )}
    </div>
  );
}
